import type { UserRole, TenantId } from '@kennisbank/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { Layout } from '../components/Layout.js';

/**
 * OneView identity card — shows the typed user projection so users can
 * verify their auth round-trip is healthy, and so admins can confirm the
 * tenant / role they're seeing the app as.
 *
 * Member-since / last sign-in come from the user doc and only exist for
 * Entra sign-ins (dev tokens have no user doc), so those rows are hidden
 * when absent rather than showing a permanent dash — we don't fabricate.
 */

// HQ timezone for human-readable timestamps (CLAUDE.md). "Africa/Johannesburg"
// is the IANA name Intl accepts; there is no "Europe/Johannesburg" zone.
const DISPLAY_TZ = 'Africa/Johannesburg';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    timeZone: DISPLAY_TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    timeZone: DISPLAY_TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function roleLabel(role: UserRole): string {
  switch (role) {
    case 'kb_admin':
      return 'Admin';
    case 'sensitive_user':
      return 'Sensitive';
    case 'confidential_user':
      return 'Confidential';
    case 'exco_user':
      return 'Exco';
    case 'kb_user':
      return 'User';
    default:
      return String(role);
  }
}

function tenantDisplay(tenantId: TenantId | string): string {
  if (tenantId === 'onenebula' || tenantId === 'suppohrt') return 'OneNebula';
  if (tenantId === '_platform') return 'Platform';
  return String(tenantId).toUpperCase();
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function ProfilePage(): JSX.Element {
  const { user, signOut } = useAuth();
  if (!user) return <></>;

  const accessGroups = user.accessGroups ?? [];

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 py-6">
        <header className="mb-6">
          <h1 className="ov-h-page">Profile</h1>
          <p className="ov-body mt-1 text-nebula-fg-3">
            Your account, tenant access, and session details.
          </p>
        </header>

        {/* ── Identity card ──────────────────────────────────────── */}
        <section className="max-w-2xl rounded-md border border-nebula-border-soft bg-nebula-surface p-6 shadow-card">
          <div className="flex items-start gap-4">
            {user.photoDataUrl ? (
              <img
                src={user.photoDataUrl}
                alt={user.displayName}
                className="h-14 w-14 shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-nebula-primary font-display text-2xl text-white">
                {initialsFor(user.displayName)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <h2 className="ov-h-section truncate">{user.displayName}</h2>
                <span className="shrink-0 rounded-full bg-nebula-tint px-3 py-1 font-ui text-sm text-nebula-secondary">
                  {roleLabel(user.role)}
                </span>
              </div>
              <p className="ov-body mt-1 truncate text-nebula-fg-2">{user.email}</p>
            </div>
          </div>

          <div className="my-4 border-t border-nebula-border-soft" />

          <dl className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
            {user.jobTitle && (
              <div>
                <dt className="ov-meta-upper">Job title</dt>
                <dd className="ov-body mt-1">{user.jobTitle}</dd>
              </div>
            )}
            {user.department && (
              <div>
                <dt className="ov-meta-upper">Department</dt>
                <dd className="ov-body mt-1">{user.department}</dd>
              </div>
            )}
            {user.officeLocation && (
              <div>
                <dt className="ov-meta-upper">Office</dt>
                <dd className="ov-body mt-1">{user.officeLocation}</dd>
              </div>
            )}
            <div>
              <dt className="ov-meta-upper">Tenant</dt>
              <dd className="ov-body mt-1">{tenantDisplay(user.tenantId)}</dd>
            </div>
            <div>
              <dt className="ov-meta-upper">Role</dt>
              <dd className="ov-body mt-1">{roleLabel(user.role)}</dd>
            </div>
            {user.memberSince && (
              <div>
                <dt className="ov-meta-upper">Member since</dt>
                <dd className="ov-body mt-1">{formatDate(user.memberSince)}</dd>
              </div>
            )}
            {user.lastLoginAt && (
              <div>
                <dt className="ov-meta-upper">Last sign-in</dt>
                <dd className="ov-body mt-1">{formatDateTime(user.lastLoginAt)}</dd>
              </div>
            )}
          </dl>

          <p className="ov-meta mt-6 text-nebula-fg-3">
            Single sign-on via Microsoft Entra. To change name or email, contact your tenant admin.
          </p>
        </section>

        {/* ── Access groups ──────────────────────────────────────── */}
        <section className="mt-8 max-w-2xl">
          <h2 className="ov-h-section mb-3">Access groups</h2>
          {accessGroups.length === 0 ? (
            <p className="ov-meta text-nebula-fg-3">
              No access groups assigned. Content classified confidential is only visible to members
              of an access group.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {accessGroups.map((g) => (
                <span
                  key={g}
                  className="rounded-full bg-nebula-tint px-3 py-1 font-ui text-sm text-nebula-secondary"
                >
                  {g}
                </span>
              ))}
            </div>
          )}
        </section>

        {/* ── Sign out ───────────────────────────────────────────── */}
        <div className="mt-8 max-w-2xl">
          <button
            type="button"
            onClick={() => {
              void signOut();
            }}
            className="rounded-md border border-nebula-border bg-white px-4 py-2 font-ui text-nebula-error transition hover:bg-nebula-error-bg"
          >
            Sign out
          </button>
        </div>
      </div>
    </Layout>
  );
}
